import { DatePipe } from '@angular/common';
import { Component } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '@base/base.component';
import { PortfolioActives } from '@enums/portfolio-actives';
import { CustomValidators } from '@forms/custom-validators';
import { ListPortfolioManagementCountQueryResponse } from '@models/reports/reports';
import { KeyValueOptions } from '@models/settings/settings';
import { Paginator, PaginatorItems, Table } from '@models/table';
import { ReportsService } from '@services/reports.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { PortfolioManagementDetailsReportModalComponent } from './modals/portfolio-management-details-report-modal/portfolio-management-details-report-modal.component';
import { Period } from '@models/period';
import { PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_READ, PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_READ_DETAILS } from 'src/app/const/permitions';
import { DateHelper } from '@helpers/data-helper';
import { Comissioned } from '@models/comissioned';

@Component({
  selector: 'app-portfolio-management-report',
  templateUrl: './portfolio-management-report.component.html',
  styleUrl: './portfolio-management-report.component.scss'
})
export class PortfolioManagementReportComponent extends BaseComponent {
  private acessPointChangeSubscription: Subscription;
  private firstSearchDone: boolean = false;

  public readonly table: Table<any> = this.createTable();

  public readonly activesOptions: KeyValueOptions[] = [
    {
      key: 'Carteira total',
      value: PortfolioActives.TotalPortfolio
    },
    {
      key: 'Ativos total',
      value: PortfolioActives.TotalAssets
    },
    {
      key: 'Ativos contemplados total',
      value: PortfolioActives.TotalContemplatedAssets
    },
    {
      key: 'Ativos contemplados com bens entregues',
      value: PortfolioActives.AssetsContemplatedWithAssetsDelivered
    },
    {
      key: 'contemplados com bens a entregar',
      value: PortfolioActives.AssetsContemplatedWithAssetsToBeDelivered
    },
    {
      key: 'Ativos não contemplados',
      value: PortfolioActives.AssetsNotContemplated
    },
    {
      key: 'Cancelados total',
      value: PortfolioActives.TotalCancelledAssets
    },
    {
      key: 'Cancelados com 1 parcela paga - quantidade de cotas',
      value: PortfolioActives.CancelledWith1InstallmentPaid
    },
    {
      key: 'Contemplações no mês - quantidade de cotas',
      value: PortfolioActives.ContemplationsInTheMonth
    },
    {
      key: 'Contemplados em dia - SEM o bem',
      value: PortfolioActives.ContemplatedAssetsUpToDateWithoutTheAsset
    },
    {
      key: 'Contemplados em dia - COM o bem',
      value: PortfolioActives.ContemplatedAssetsUpToDateWithTheAsset
    },
    {
      key: 'Contemplados em atraso - SEM o bem',
      value: PortfolioActives.ContemplatedInArrearsWithoutTheAsset
    },
    {
      key: 'Contemplados em atraso - COM o bem',
      value: PortfolioActives.ContemplatedInArrearsWithTheAsset
    },
    {
      key: 'Não contemplados - Em dia',
      value: PortfolioActives.NotContemplatedUpToDate
    },
    {
      key: 'Não contemplados - Em atraso',
      value: PortfolioActives.NotContemplatedInArrears
    },
    {
      key: 'Faltando 10% ou < para quitar',
      value: PortfolioActives.TenPercentOrLessLeftToPayForAsset
    },
    {
      key: 'Quitados',
      value: PortfolioActives.PaidOff
    },
    {
      key: 'Cancelados - desistentes',
      value: PortfolioActives.CanceledWithdrawals
    }
  ];

  public dealerOptions: KeyValueOptions[] = [];

  public form: FormGroup;
  public createdAt: FormGroup;
  public loading: boolean = false;

  public constructor(private readonly _fb: FormBuilder,
    private readonly _reportsService: ReportsService,
  ) {
    super();
  }

  public ngOnInit() {
    this.listDealers();
    this.createForm();
    this.acessPointChangeSubscription = this.acessPointChangeEvent().subscribe(() => this.listDealers())

    if (!this.hasAccessToAnyDealer) {
      this.openDialogMessage("Não foram encontrados nenhum ponto de venda para sua conta, por esse motivo vamos bloquear a pesquisa deste relatório.")
    }
  }

  public async listDealers() {
    const allowedDealersOptions = this.getAllAllowedDealersForCurrentHierachy()
      .filter((item) => item.isEconomicGroup === false)
      .map<KeyValueOptions>(us => ({
        key: `${us.identifierCode} - ${us.description}`,
        value: us.identifierCode
      }));

    this.dealerOptions = [
      {
        key: "Todos",
        value: 0
      }, ...allowedDealersOptions];

    if (this.form) {
      this.form.patchValue({
        identifierCode: 0
      });
    }
  }

  public get hasAccessToAnyDealer() {
    return this.getAllAllowedDealers().length > 0;
  }

  public get hasPermissionToReadDetails(): boolean {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_READ_DETAILS].includes(p))
  }

  public get hasPermissionToRead(): boolean {
    return this.getPermissions()
      .some(p => [PERMITION_CONSORTIUM_REPORTS_PORTFOLIO_MANAGEMENT_READ].includes(p));
  }

  public openDetails(row: ListPortfolioManagementCountQueryResponse) {
    const { active, name } = row;
    const { initialDate, endDate, identifierCode } = this.form.value;

    const selledPeriod: Period = {
      startDate: initialDate,
      endDate
    }

    this.openDialog(PortfolioManagementDetailsReportModalComponent, {
      data: {
        active,
        selledAt: selledPeriod,
        identifierCode,
        activeName: name
      },
    })
  }

  private createForm() {
    this.form = this._fb.group({
      actives: [this.activesOptions, [Validators.required]],
      identifierCode: [0, [Validators.required]],
      initialDate: [DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 29), [Validators.required]],
      endDate: [DateHelper.currentDate, [Validators.required]]
    });

    this.form.setValidators([
      CustomValidators.isStartDateGreaterThanEndDate("initialDate", "endDate"),
      CustomValidators.shouldBeRequiredIfFieldIsNotNull("initialDate", "endDate"),
      CustomValidators.shouldBeRequiredIfFieldIsNotNull("endDate", "initialDate"),
      CustomValidators.periodGreaterThanFor(30, 'initialDate', 'endDate')
    ])
  }

  public filter(paginator?: Paginator): void {
    if (!this.hasAccessToAnyDealer || this.form.invalid) {
      return;
    }

    this.loading = true;

    if (!paginator) {
      paginator = this.table.paginator;
    }

    const form = {
      ...this.form.value,
      actives: this.form.get("actives").value.some(v => typeof (v) === "object") ? this.form.get("actives").value.map(v => v.value) : this.form.get("actives").value
    }

    firstValueFrom(
      this._reportsService.getPortfolioReport(form, paginator, this.firstSearchDone)
    )
      .then((resp: PaginatorItems<any>) => {
        this.table.data = resp.items;
        this.table.paginator.length = resp.totalCount;
        this.table.paginator.index = paginator.index;
        this.table.paginator.pageSize = paginator.pageSize;
      })
      .finally(() => {
        this.loading = false;
        this.firstSearchDone = true;
      });
  }

  public cleanForm(): void {
    this.form.patchValue({
      actives: this.activesOptions,
      identifierCode: 0,
      initialDate: DateHelper.SubstractDaysFromDate(DateHelper.currentDate, 29),
      endDate: DateHelper.currentDate
    });

    this.table.data = [];
  }

  public pageChange(paginator: Paginator): void {
    this.filter(paginator);
  }


  private createTable(): Table<any> {
    return {
      headers: [
        {
          key: "name",
          value: "Dados Cadastrais",
        },
        {
          key: "activesCount",
          value: "Quantidade",
        },
      ],
      data: [],
      style: {
        table: {
          height: "500px",
        },
        data: []
      },
      checkbox: false,
      buttons: this.hasAccessToAnyDealer && this.hasPermissionToReadDetails && {
        read: this.hasPermissionToReadDetails
      },
      paginator: {
        index: 1,
        itensPerPage: [10, 15, 20, 50, 100],
        pageSize: 10,
      },
      messageWithoutDates: "Não há dados para exibir no momento.",
    };
  }
}
